<script setup>
import { computed } from 'vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import { formatMoney, formatDeduction } from '@/helpers/billing-format'

/**
 * What the next invoice will be, worked out live by the API from the same
 * calculation that finalizes it: line items, discount, credit, tax, total, the
 * date and the card it will go to. During a trial there is nothing due yet, so
 * it says when the trial ends and what happens then instead.
 */
const emit = defineEmits(['update-payment-method'])

const billingStore = useBillingStore()
const { can } = usePermissions()

const upcoming = computed(() => billingStore.upcomingInvoice)
const loading = computed(() => billingStore.loading.upcomingInvoice)
const error = computed(() => billingStore.errors.upcomingInvoice)
const trial = computed(() => billingStore.summary?.subscription?.trial ?? null)
const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))

const invoice = computed(() => upcoming.value?.invoice ?? null)
const outcome = computed(() => upcoming.value?.outcome ?? null)
const currency = computed(() => invoice.value?.currency ?? 'usd')
const isTrial = computed(() => !!trial.value || !!upcoming.value?.ends_trial)
const hasCard = computed(() => !!upcoming.value?.payment_method)

// Nothing renews while the account is suspended or cancelled; a cancel or a
// move to a Free plan leaves nothing to charge.
const nothingToCharge = computed(
    () => !invoice.value || invoice.value.total === 0
)

const money = (cents) => formatMoney(cents, currency.value)

// The lines the invoice is made of, then what comes off it.
const adjustments = computed(() => {
    if (!invoice.value) return []

    const rows = []
    if (invoice.value.discount > 0) {
        rows.push({
            key: 'discount',
            label: 'Discount',
            amount: formatDeduction(invoice.value.discount, currency.value),
        })
    }
    if (invoice.value.credit_applied > 0) {
        rows.push({
            key: 'credit',
            label: 'Credit',
            amount: formatDeduction(
                invoice.value.credit_applied,
                currency.value
            ),
        })
    }
    if (invoice.value.tax > 0 || invoice.value.reverse_charge) {
        rows.push({
            key: 'tax',
            label: invoice.value.reverse_charge
                ? 'Tax (reverse charge)'
                : 'Tax',
            amount: money(invoice.value.tax),
        })
    }

    return rows
})

// What the owner is told when the invoice isn't a charge.
const noChargeCopy = computed(() => {
    switch (outcome.value) {
        case 'none':
            return "There's no next invoice. Nothing renews until your account is active again."
        case 'cancels_to_free':
            return "Your plan is cancelled, so there's no next invoice. You move to Free."
        case 'trial_free_plan':
            return 'Nothing to pay. You move to Free when your trial ends.'
        default:
            if (upcoming.value?.plan?.is_free) return 'Nothing to pay on Free.'
            return invoice.value
                ? 'Nothing to pay. Your credit and discount cover this invoice.'
                : "There's no next invoice."
    }
})
</script>

<template>
    <section
        data-testid="upcoming-invoice-card"
        aria-labelledby="upcoming-invoice-heading"
        class="flex h-full flex-col rounded-xl border border-taupe-200 bg-white p-6 shadow-sm"
    >
        <h2
            id="upcoming-invoice-heading"
            class="text-sm font-medium uppercase tracking-wide text-taupe-600"
        >
            Next invoice
        </h2>

        <div
            v-if="loading && !upcoming"
            class="mt-4 space-y-3"
            data-testid="upcoming-invoice-loading"
        >
            <zen-skeleton class="h-8 w-40" />
            <zen-skeleton class="h-4 w-full" />
            <zen-skeleton class="h-4 w-2/3" />
        </div>

        <div
            v-else-if="error && !upcoming"
            class="mt-4 space-y-3"
            data-testid="upcoming-invoice-error"
        >
            <p class="text-sm text-red-700">
                We couldn't work out your next invoice. {{ error }}
            </p>
            <zen-button
                variant="secondary"
                size="small"
                @click="billingStore.refreshUpcomingInvoice()"
            >
                Try again
            </zen-button>
        </div>

        <template v-else-if="upcoming">
            <!-- A trial: nothing is due until it ends -->
            <div
                v-if="isTrial"
                class="mt-4 space-y-3"
                data-testid="upcoming-trial"
            >
                <div class="text-2xl font-semibold text-taupe-900">
                    Nothing due
                </div>
                <p class="text-sm text-taupe-600">
                    Your trial ends on
                    <span class="font-medium text-taupe-900"
                        ><zen-date-time
                            :value="upcoming.invoice_at"
                            format="date"
                            utc /></span
                    >.
                </p>
                <p
                    v-if="outcome === 'trial_charge'"
                    class="text-sm text-taupe-600"
                    data-testid="upcoming-trial-outcome"
                >
                    Then we charge
                    <span class="font-medium text-taupe-900">{{
                        money(invoice?.total)
                    }}</span>
                    to your {{ upcoming.payment_method?.description || 'card'
                    }}<template v-if="trial?.converts_to">
                        for {{ trial.converts_to.plan_name }}</template
                    >.
                </p>
                <div
                    v-else-if="outcome === 'trial_free_no_card'"
                    class="space-y-2"
                    data-testid="upcoming-trial-outcome"
                >
                    <p class="text-sm text-taupe-600">
                        You have no card on file, so you move to Free when your
                        trial ends<template v-if="trial?.converts_to">
                            instead of
                            {{ trial.converts_to.plan_name }}</template
                        >.
                    </p>
                    <zen-button
                        v-if="canManage"
                        variant="secondary"
                        size="small"
                        data-testid="upcoming-add-card"
                        @click="emit('update-payment-method')"
                    >
                        Add a card
                    </zen-button>
                </div>
                <p
                    v-else
                    class="text-sm text-taupe-600"
                    data-testid="upcoming-trial-outcome"
                >
                    {{ noChargeCopy }}
                </p>
            </div>

            <!-- Nothing to charge -->
            <div
                v-else-if="nothingToCharge"
                class="mt-4 space-y-2"
                data-testid="upcoming-nothing"
            >
                <div class="text-2xl font-semibold text-taupe-900">
                    Nothing due
                </div>
                <p class="text-sm text-taupe-600">{{ noChargeCopy }}</p>
            </div>

            <!-- The invoice -->
            <template v-else>
                <div class="mt-4 flex items-baseline gap-2">
                    <div class="text-3xl font-semibold text-taupe-900">
                        <zen-date-time
                            :value="upcoming.invoice_at"
                            format="date"
                            utc
                        />
                    </div>
                    <div
                        v-if="upcoming.payment_method"
                        class="text-sm text-taupe-600"
                        data-testid="upcoming-card"
                    >
                        · {{ upcoming.payment_method.description }}
                    </div>
                </div>

                <dl
                    class="mt-5 space-y-2 border-t border-taupe-200 pt-4 text-sm"
                    data-testid="upcoming-lines"
                >
                    <div
                        v-for="(line, index) in invoice.lines"
                        :key="`${line.kind}-${index}`"
                        class="flex justify-between gap-4"
                    >
                        <dt class="min-w-0 text-taupe-700">
                            {{ line.description }}
                        </dt>
                        <dd class="shrink-0 text-taupe-900">
                            {{ money(line.amount) }}
                        </dd>
                    </div>
                    <div
                        v-for="row in adjustments"
                        :key="row.key"
                        class="flex justify-between gap-4"
                        :data-testid="`upcoming-${row.key}`"
                    >
                        <dt class="text-taupe-700">{{ row.label }}</dt>
                        <dd class="shrink-0 text-taupe-900">
                            {{ row.amount }}
                        </dd>
                    </div>
                    <div
                        class="flex justify-between gap-4 border-t border-taupe-200 pt-2 text-base font-semibold"
                    >
                        <dt class="text-taupe-900">Total</dt>
                        <dd class="text-taupe-900" data-testid="upcoming-total">
                            {{ money(invoice.total) }}
                        </dd>
                    </div>
                </dl>

                <div
                    v-if="upcoming.charges_card && !hasCard"
                    class="mt-4 space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
                    data-testid="upcoming-no-card"
                >
                    <p>
                        There's no card on file to pay this. Add one before
                        <zen-date-time
                            :value="upcoming.invoice_at"
                            format="date"
                            utc
                        />.
                    </p>
                    <zen-button
                        v-if="canManage"
                        variant="secondary"
                        size="small"
                        data-testid="upcoming-add-card"
                        @click="emit('update-payment-method')"
                    >
                        Add a card
                    </zen-button>
                </div>
            </template>
        </template>

        <p v-else class="mt-4 text-sm text-taupe-600">
            There's no upcoming invoice to show yet.
        </p>
    </section>
</template>
